import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  components,
  SingleValue,
  type DropdownIndicatorProps,
} from "react-select";

import type { Class } from "types/Class";
import {
  buildClassOptions,
  getAllClassesCached,
  getAllClassesCachedSnapshot,
} from "services/classOptionServices";
import ArrowDownIcon from "assets/icons/ArrowDownIcon";
import ArrowUpIcon from "assets/icons/ArrowUpIcon";

interface ClassOption {
  label: string;
  value: string;
}

const DropdownIndicator = (
  props: DropdownIndicatorProps<ClassOption, false>,
) => (
  <components.DropdownIndicator {...props}>
    <div className="flex h-full items-center justify-center gap-2">
      <span aria-hidden="true" className="dropdown-chevron-divider" />
      {props.selectProps.menuIsOpen ? (
        <ArrowUpIcon width={24} height={24} color={"#767676"} />
      ) : (
        <ArrowDownIcon width={24} height={24} color={"#767676"} />
      )}
    </div>
  </components.DropdownIndicator>
);

interface ClassDropdownProps {
  currentClassId: string;
  disable?: boolean;
  onChange: (classId: number) => void;
}

const ClassDropdown: React.FC<ClassDropdownProps> = ({
  currentClassId,
  disable = false,
  onChange,
}) => {
  const { t } = useTranslation();
  const initialClasses = getAllClassesCachedSnapshot();
  const hasInitialClasses = initialClasses.length > 0;
  const [classes, setClasses] = useState<Class[]>(initialClasses);
  const [isLoading, setIsLoading] = useState(initialClasses.length === 0);

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!hasInitialClasses) {
        setIsLoading(true);
      }

      try {
        const cachedClasses = await getAllClassesCached();
        if (!isMounted) return;

        setClasses(cachedClasses);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [hasInitialClasses]);

  const classOptions: ClassOption[] = useMemo(
    () => buildClassOptions(classes, "desc"),
    [classes],
  );

  const selectedOption =
    classOptions.find((option) => option.value === currentClassId) || null;

  const handleChange = (selectedOption: SingleValue<ClassOption>) => {
    if (selectedOption && !isNaN(parseInt(selectedOption.value))) {
      onChange(parseInt(selectedOption.value));
    }
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: "0.375rem",
      paddingLeft: "0.75rem",
      paddingRight: "0",
      borderColor: "#9B9B9B",
      boxShadow: "none",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: "#9B9B9B",
      },
      minHeight: "46px",
      height: "46px",
      overflow: "hidden",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 10,
      marginTop: "0.25rem",
      backgroundColor: "white",
      border: "1px solid #9B9B9B",
      borderRadius: "0.375rem",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "16rem",
      overflowY: "auto",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9ca3af",
      cursor: "pointer",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0",
      minHeight: "46px",
      overflow: "hidden",
      cursor: "pointer",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      margin: 0,
      cursor: "pointer",
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
      cursor: "pointer",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      width: "48px",
      minWidth: "48px",
      display: "flex",
      alignItems: "stretch",
      justifyContent: "center",
      backgroundColor: "#FFFFFF",
      alignSelf: "stretch",
      cursor: "pointer",
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#767676",
      padding: 0,
      width: "100%",
      height: "100%",
      cursor: "pointer",
      "&:hover": {
        color: "#767676",
      },
    }),
    option: (provided: any) => ({
      ...provided,
      cursor: "pointer",
    }),
  };

  return (
    <div className="w-full h-full flex">
      <Select<ClassOption>
        value={selectedOption}
        onChange={handleChange}
        options={classOptions}
        placeholder={t("_published_picture._class")}
        isDisabled={disable || (isLoading && classOptions.length === 0)}
        isLoading={isLoading}
        loadingMessage={() => t("_generic._loading")}
        noOptionsMessage={() => t("_generic._no_data")}
        styles={customStyles}
        components={{ DropdownIndicator }}
        classNamePrefix="react-select"
        isClearable={false}
        isSearchable={false}
        className="w-full"
      />
    </div>
  );
};

export default ClassDropdown;
